/**
 * The GDPR / T&C consent decision for a CRM embed, as pure functions.
 *
 * Extracted from the legacy `EmbedConsent.context` so PF v2 can reach the same
 * answer while the two flows run side by side: these rules decide whether an
 * order may be placed without a recorded acceptance, so two implementations
 * that drift is a legal problem, not a cosmetic one.
 *
 * The wiring — which workspace, which channel, when to fetch — stays with each
 * flow's own provider; only the decision lives here.
 */

import { ConsentType } from 'models/consent'
import type { ConsentStatusDTO } from 'models/consent'

/** Every document an acceptance has to cover when the missing set is unknown. */
const ALL_CONSENT_TYPES = [ConsentType.TERMS_AND_CONDITIONS, ConsentType.PRIVACY_POLICY]

/**
 * The documents the client has not accepted at their current version.
 *
 * @example
 * missingConsentTypes(query.data)  // [ConsentType.PRIVACY_POLICY]
 */
export const missingConsentTypes = (statuses: ReadonlyArray<ConsentStatusDTO> | undefined): ConsentType[] =>
  (statuses ?? []).filter((status) => !status.upToDate).map((status) => status.consentType)

/**
 * Whether consent must be asked for before this order can be placed.
 *
 * FAILS CLOSED on a status we could not read: re-asking is idempotent and
 * harmless, whereas skipping it could place an order without a legally required
 * consent.
 */
export const isConsentRequired = (
  input: { isApplicable: boolean, hasError: boolean, missing: ReadonlyArray<ConsentType> }
): boolean => input.isApplicable && (input.hasError || input.missing.length > 0)

/**
 * What to send to the record endpoint. An empty missing set here means the
 * fail-closed path (the status fetch failed, so what is missing is unknown) —
 * record everything rather than guess.
 */
export const consentTypesToRecord = (missing: ReadonlyArray<ConsentType>): ConsentType[] =>
  missing.length > 0 ? [...missing] : [...ALL_CONSENT_TYPES]
